<script setup>
import { computed, ref, watch, onUnmounted } from 'vue';
import { useElementVisibility } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  isWorkspaceV2Path,
  workspaceV2Path,
} from 'dashboard/helper/workspaceV2';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import KanbanAPI from 'dashboard/api/kanban';
import { kanbanMessageLinks } from './kanbanMessageLinks';

const props = defineProps({
  content: { type: String, default: '' },
  accountId: { type: [String, Number], required: true },
  userId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const route = useRoute();
const cardHref = card =>
  isWorkspaceV2Path(route.path) ? workspaceV2Path(card.href) : card.href;
const root = ref(null);
const visible = useElementVisibility(root);
const links = computed(() =>
  kanbanMessageLinks(props.content, {
    origin: window.location.origin,
    accountId: props.accountId,
  })
);
const cards = ref([]);
const refreshing = ref(false);
let generation = 0;
let pendingRequestKey = null;
let pendingChanged = false;
watch(
  [links, () => props.accountId, () => props.userId],
  () => {
    generation += 1;
    pendingRequestKey = null;
    pendingChanged = false;
    refreshing.value = false;
    cards.value = [];
  },
  { flush: 'sync' }
);
const refreshCards = async () => {
  if (!visible.value || links.value.length === 0) return;
  if (cards.value.some(card => card.busy)) return;
  const requestKey = JSON.stringify({
    accountId: props.accountId,
    userId: props.userId,
    itemIds: links.value.map(link => link.itemId),
  });
  if (pendingRequestKey === requestKey) return;

  pendingRequestKey = requestKey;
  refreshing.value = true;
  generation += 1;
  const request = generation;
  const previousCards = new Map(
    cards.value.map(card => [card.itemId, card.item])
  );
  const result = await Promise.all(
    links.value.map(async link => {
      try {
        const { data } = await KanbanAPI.getItem(link.itemId);
        return { ...link, item: data };
      } catch (error) {
        const denied = [401, 403, 404].includes(error.response?.status);
        return {
          ...link,
          item: denied ? null : previousCards.get(link.itemId) || null,
          retryable: !denied,
        };
      }
    })
  );
  if (request === generation) cards.value = result;
  if (pendingRequestKey === requestKey && request === generation) {
    pendingRequestKey = null;
    refreshing.value = false;
    if (pendingChanged && !cards.value.some(card => card.busy)) {
      pendingChanged = false;
      refreshCards();
    }
  }
};

watch([links, visible, () => props.userId], refreshCards, { immediate: true });

const refreshWhenActive = () => {
  if (document.visibilityState === 'hidden') return;
  refreshCards();
};

const retryCards = () => refreshCards();

window.addEventListener('focus', refreshWhenActive);
document.addEventListener('visibilitychange', refreshWhenActive);
const stageName = card =>
  card.item?.funnel?.stages?.[card.item.funnel_stage]?.name;
const assignedToMe = card =>
  (card.item?.assigned_agents || []).some(
    agent => String(agent.id) === String(props.userId)
  );
const assignToMe = async card => {
  if (
    refreshing.value ||
    card.busy ||
    card.retryable ||
    assignedToMe(card) ||
    !card.item?.access?.can_assign
  )
    return;
  const request = generation;
  card.busy = true;
  card.error = false;
  try {
    const { data } = await KanbanAPI.assignAgent(card.itemId, props.userId);
    if (request === generation) card.item = data;
  } catch (error) {
    if (request === generation) {
      if ([401, 403, 404].includes(error.response?.status)) card.item = null;
      card.error = true;
    }
  } finally {
    if (request === generation) {
      card.busy = false;
      if (pendingChanged && !cards.value.some(item => item.busy)) {
        pendingChanged = false;
        refreshCards();
      }
    }
  }
};
const onAccessChanged = event => {
  if (
    String(event.account_id) !== String(props.accountId) ||
    String(event.user_id) !== String(props.userId) ||
    !links.value.some(
      link => String(link.itemId) === String(event.kanban_item_id)
    )
  )
    return;
  generation += 1;
  pendingRequestKey = null;
  pendingChanged = false;
  refreshing.value = false;
  cards.value = [];
  refreshCards();
};
emitter.on(BUS_EVENTS.KANBAN_ITEM_ACCESS_CHANGED, onAccessChanged);
const onItemChanged = event => {
  const refreshAll = event.all === true;
  if (
    String(event.account_id) !== String(props.accountId) ||
    (!refreshAll &&
      !links.value.some(link => String(link.itemId) === String(event.id)))
  )
    return;
  if (refreshAll) {
    generation += 1;
    pendingRequestKey = null;
    pendingChanged = false;
    refreshing.value = false;
    cards.value = [];
    refreshCards();
    return;
  }
  if (refreshing.value || cards.value.some(card => card.busy)) {
    pendingChanged = true;
    return;
  }
  generation += 1;
  pendingRequestKey = null;
  refreshing.value = false;
  refreshCards();
};
emitter.on(BUS_EVENTS.KANBAN_ITEM_CHANGED, onItemChanged);
onUnmounted(() => {
  emitter.off(BUS_EVENTS.KANBAN_ITEM_ACCESS_CHANGED, onAccessChanged);
  emitter.off(BUS_EVENTS.KANBAN_ITEM_CHANGED, onItemChanged);
  generation += 1;
  pendingRequestKey = null;
  pendingChanged = false;
  window.removeEventListener('focus', refreshWhenActive);
  document.removeEventListener('visibilitychange', refreshWhenActive);
});
</script>

<template>
  <div
    v-if="links.length"
    ref="root"
    class="mt-2 flex min-h-11 min-w-0 flex-col gap-2"
  >
    <article
      v-for="card in cards"
      :key="card.itemId"
      class="flex min-h-11 max-w-full flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-2 p-3 text-n-slate-12 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-9"
    >
      <span class="flex items-center gap-2 text-xs text-n-slate-11">
        <span class="i-ph-kanban size-4 shrink-0" aria-hidden="true" />
        {{ t('INTERNAL_CHAT.KANBAN_CARD.LABEL') }}
      </span>
      <span
        v-if="card.item || !card.retryable"
        class="break-words text-sm font-medium"
      >
        {{
          card.item?.item_details?.title ||
          t(
            card.retryable
              ? 'INTERNAL_CHAT.KANBAN_CARD.LABEL'
              : 'INTERNAL_CHAT.KANBAN_CARD.UNAVAILABLE'
          )
        }}
      </span>
      <p v-if="card.retryable" role="alert" class="m-0 text-xs text-n-amber-11">
        {{ t('INTERNAL_CHAT.KANBAN_CARD.LOAD_ERROR') }}
      </p>
      <p
        v-if="stageName(card)"
        class="m-0 flex items-start gap-2 text-xs text-n-slate-11"
      >
        <span class="i-ph-columns size-4 shrink-0" aria-hidden="true" />
        <span class="min-w-0 break-words">{{ stageName(card) }}</span>
      </p>
      <p
        v-if="card.item"
        class="m-0 flex items-start gap-2 text-xs text-n-slate-11"
      >
        <span class="i-ph-users size-4 shrink-0" aria-hidden="true" />
        <span class="min-w-0 break-words">
          {{
            (card.item.assigned_agents || [])
              .map(agent => agent.name)
              .filter(Boolean)
              .join(', ') || t('INTERNAL_CHAT.KANBAN_CARD.NO_ASSIGNEES')
          }}
        </span>
      </p>
      <div class="flex flex-wrap gap-2">
        <button
          v-if="card.retryable"
          type="button"
          class="inline-flex min-h-11 items-center gap-2 rounded-xl bg-n-alpha-2 px-3 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-9"
          :disabled="refreshing"
          :aria-busy="refreshing"
          @click="retryCards"
        >
          {{ t('INTERNAL_CHAT.KANBAN_CARD.RETRY') }}
        </button>
        <a
          :href="cardHref(card)"
          class="inline-flex min-h-11 items-center gap-2 rounded-xl px-2 text-xs font-medium text-n-blue-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-9"
        >
          {{ t('INTERNAL_CHAT.KANBAN_CARD.OPEN') }}
          <span class="i-ph-arrow-up-right size-4" aria-hidden="true" />
        </a>
        <button
          v-if="card.item?.access?.can_assign"
          type="button"
          :disabled="
            refreshing || card.busy || card.retryable || assignedToMe(card)
          "
          :aria-busy="!!card.busy"
          class="inline-flex min-h-11 items-center gap-2 rounded-xl bg-n-alpha-2 px-3 text-xs font-medium disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-9"
          @click="assignToMe(card)"
        >
          <span class="i-ph-user-plus size-4" aria-hidden="true" />
          {{
            t(
              assignedToMe(card)
                ? 'INTERNAL_CHAT.KANBAN_CARD.ASSIGNED'
                : 'INTERNAL_CHAT.KANBAN_CARD.ASSIGN'
            )
          }}
        </button>
      </div>
      <p v-if="card.error" role="alert" class="text-xs text-n-ruby-11">
        {{ t('INTERNAL_CHAT.KANBAN_CARD.ERROR') }}
      </p>
    </article>
  </div>
</template>
